import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
// Import Types
import type {
    ListHeaderAction,
    ListHeaderBuiltInAction,
    ListHeaderCustomAction,
    PartnerSettingsPermissionsKeys,
    ResolvedListHeaderAction,
} from 'types';
// Import Enums
import { ListHeaderActionPlacementEnum, ListHeaderActionTypeEnum } from 'enums';

export interface ListHeaderAccessCheckers {
    hasPermission: (permissions: string | string[], isAllMandatory?: boolean) => boolean;
    hasPartnerSettings: (settingsKey: PartnerSettingsPermissionsKeys) => boolean;
}

export interface ResolveListHeaderActionsParams extends ListHeaderAccessCheckers {
    actions: ListHeaderAction[];
    /** Turns a built-in descriptor into a renderable action; return `null` to drop it. */
    getBuiltInAction: (
        action: ListHeaderBuiltInAction,
        placement: ListHeaderActionPlacementEnum,
    ) => Nullable<ResolvedListHeaderAction>;
}

/**
 * Same access hierarchy as `AccessControl`: partner setting, then all mandatory permissions, then any permission.
 */
export const hasListHeaderActionAccess = (
    action: ListHeaderAction,
    { hasPermission, hasPartnerSettings }: ListHeaderAccessCheckers,
): boolean => {
    const { partnerSetting, mandatoryPermissions = [], permissions = [] } = action;

    if (!isNil(partnerSetting) && !hasPartnerSettings(partnerSetting)) {
        return false;
    }

    if (!isEmpty(mandatoryPermissions) && !hasPermission(mandatoryPermissions, true)) {
        return false;
    }

    if (!isEmpty(permissions) && !hasPermission(permissions)) {
        return false;
    }

    return true;
};

const resolveCustomListHeaderAction = (
    action: ListHeaderCustomAction,
    placement: ListHeaderActionPlacementEnum,
): Nullable<ResolvedListHeaderAction> => {
    const { id, node, render, label, icon, onClick, isDisabled } = action;

    if (placement === ListHeaderActionPlacementEnum.Menu) {
        if (isNil(label) || isNil(onClick)) {
            console.warn(`ListHeader: custom action "${id}" placed in the menu needs both "label" and "onClick".`);
            return null;
        }

        return { id, placement, menuItem: { label, icon, onClick, isDisabled } };
    }

    const headerNode = node ?? render?.(placement);

    return isNil(headerNode) ? null : { id, placement, headerNode };
};

/**
 * Drops hidden / unauthorized actions and resolves the rest in the configured order.
 */
export const resolveListHeaderActions = ({
    actions,
    getBuiltInAction,
    ...accessCheckers
}: ResolveListHeaderActionsParams): ResolvedListHeaderAction[] =>
    actions.reduce<ResolvedListHeaderAction[]>((acc, action) => {
        if (action.isHidden === true || !hasListHeaderActionAccess(action, accessCheckers)) {
            return acc;
        }

        const placement = action.placement ?? ListHeaderActionPlacementEnum.Header;
        const resolved =
            action.type === ListHeaderActionTypeEnum.Custom
                ? resolveCustomListHeaderAction(action, placement)
                : getBuiltInAction(action, placement);

        if (!isNil(resolved)) {
            acc.push(resolved);
        }

        return acc;
    }, []);

export interface SearchableRow {
    data?: Record<string, unknown>;
}

/**
 * Client-side row filter, a port of gene-ui `ComboTable`'s built-in search:
 * case-insensitive `includes` over string / number values of the given data keys.
 */
export const filterRowsByQuery = <T extends SearchableRow>(rows: T[], query: string, dataKeys: string[]): T[] => {
    const lowerCaseQuery = query.trim().toLocaleLowerCase();

    if (lowerCaseQuery === '') {
        return rows;
    }

    return rows.filter(({ data }) =>
        dataKeys.some((key) => {
            const value = data?.[key];

            if (typeof value === 'number') {
                return value.toString().includes(lowerCaseQuery);
            }

            if (typeof value === 'string') {
                return value.toLocaleLowerCase().includes(lowerCaseQuery);
            }

            return false;
        }),
    );
};
